/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injector} from '@angular/core';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import {ObjectTypeFinder} from '../common/constants/object-type';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {Uuid} from '../../components/util/uuid';
import {AdnRouterHelperApi, AdnRouterStateHelper} from './state-utils';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {FeatureStateDependencies} from './feature-state-dependencies';

import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';

export function resolveListReadOnly(
  readOnly: boolean,
  sectionParam: string,
  LocalUserPermissions: any,
  LocalUserProfile?: {isReadOnly(): boolean}
): boolean {
  if (LocalUserProfile?.isReadOnly()) {
    return true;
  }
  const sectionPerms = LocalUserPermissions.getSectionPerms();
  const specificEditableParameter = sectionParam + 'Editable';
  if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, specificEditableParameter)) {
    return !sectionPerms[specificEditableParameter];
  }
  if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, sectionParam)) {
    return !sectionPerms[sectionParam];
  }
  return !!readOnly;
}

export function createAdnRouterHelper(ng2Injector: Injector): AdnRouterHelperApi {
  function getResource(resourceProvider: any): any {
    return ng2Injector.get(resourceProvider);
  }

  function createRouterStateHelper(
    states: string,
    resourceProvider: any,
    childResourceProvider: any | null,
    parent: string,
    _dir: string,
    _listTemplate: string
  ): AdnRouterStateHelper {
    const rh = {} as AdnRouterStateHelper;
    const splitStates = states.split('.');
    const statePlural = splitStates[0];
    const state = splitStates[1];

    rh.listPath = function() {
      return parent + '.' + statePlural;
    };
    rh.list = function(extras: any) {
      const views = {
        'content@app': {},
        'context@app': {
          component: ListDefaultActionsComponent,
          bindings: {
            readOnly: 'listReadOnly',
            newStateName: 'listNewStateName'
          }
        },
        'title@app': stateTitleView()
      };
      const resolve = _.merge({}, {
        ResourceFactory: function() {
          return getResource(resourceProvider);
        },
        ChildResourceFactory: function() {
          return childResourceProvider ? getResource(childResourceProvider) : null;
        },
        orderByParam: function() {
          return 'name';
        },
        listConfig: function() {
        },
        objectType: function() {
          return ObjectTypeFinder.findObjectFromStateName(states);
        },
        sectionParam: function() {
        },
        listReadOnly: function(readOnly: boolean, sectionParam: string) {
          return resolveListReadOnly(readOnly, sectionParam, ng2Injector.get(LocalUserPermissions));
        },
        listNewStateName: function() {
          return rh.newItemPath();
        }
      }, _.get(extras, 'resolve', {}));
      const configuredListReadOnly = resolve.listReadOnly;
      resolve.configuredListReadOnly = configuredListReadOnly;
      resolve.listReadOnly = function(configuredListReadOnly: boolean) {
        return ng2Injector.get(LocalUserProfile).isReadOnly() || !!configuredListReadOnly;
      };
      return {
        url: '/' + statePlural,
        resolve,
        views: _.assign({}, views, _.get(extras, 'views', {})),
        data: _.assign({}, _.get(extras, 'data', {}), {
          title: _.camelCase(state) + ".plural"
        })
      };
    };

    rh.newPath = function(pathState: string) {
      return parent + '.' + statePlural + '.' + pathState;
    };

    rh.newItemPath = function() {
      return parent + '.' + statePlural + '.new';
    };
    rh.newItem = function(extras: any) {
      return {
        url: '/new',
        params: _.merge({}, {
          copyId: false, folioId: false, folioName: false, orderId: false, orderName: false, full: false, viewType: false,
          advertiserId: false, advertiserName: false, earningsAccountId: false, earningsAccountName: false,
          earningsAccountCountry: false, earningsAccountOwnerTeamId: false
        }, extras || {}),
        views: {},
        onEnter: function($transition$: any) {
          const $stateParams = $transition$.params('to');
          const passExtras: any = {};
          if (extras) {
            _.forEach(extras, function(v, k) {
              passExtras[k] = $stateParams[k];
            });
          }
          if ($stateParams.copyId) {
            passExtras.copyId = $stateParams.copyId;
          }
          if ($stateParams.orderId && $stateParams.orderName) {
            passExtras.orderId = $stateParams.orderId;
            passExtras.orderName = $stateParams.orderName;
          }
          if ($stateParams.advertiserId && $stateParams.advertiserName) {
            passExtras.advertiserId = $stateParams.advertiserId;
            passExtras.advertiserName = $stateParams.advertiserName;
          }
          if ($stateParams.folioId && $stateParams.folioName) {
            passExtras.folioId = $stateParams.folioId;
            passExtras.folioName = $stateParams.folioName;
          }
          if ($stateParams.earningsAccountId && $stateParams.earningsAccountName) {
            passExtras.earningsAccountId = $stateParams.earningsAccountId;
            passExtras.earningsAccountName = $stateParams.earningsAccountName;
            passExtras.earningsAccountCountry = $stateParams.earningsAccountCountry;
            passExtras.earningsAccountOwnerTeamId = $stateParams.earningsAccountOwnerTeamId;
          }
          if ($stateParams.viewType) {
            passExtras.viewType = $stateParams.viewType;
          }
          if ($stateParams.full) {
            passExtras.full = $stateParams.full;
          }
          return ng2Injector.get(StateService).target(
            parent + '.' + states,
            _.merge({}, {id: Uuid.generate(), isNew: true}, passExtras || {})
          );
        }
      };
    };

    rh.itemPath = function() {
      return parent + '.' + states;
    };

    rh.item = function(aExtras: any) {
      const views = {
        'content@app': {},
        'context@app': {},
        'title@app': {}
      };
      return {
        url: _.get(aExtras, 'url') || '/' + state + '/:id',
        params: _.merge({}, {
          isNew: false, full: false, viewType: false, copyId: false, folioId: false, folioName: false, orderId: false,
          orderName: false, advertiserId: false, advertiserName: false, earningsAccountId: false, earningsAccountName: false,
          earningsAccountCountry: false, earningsAccountOwnerTeamId: false
        }, _.get(aExtras, 'params', {})),
        resolve: _.merge({}, {
          isNew: function($stateParams: any) {
            return !!$stateParams.isNew;
          },
          model: function($stateParams: any) {
            const resource = getResource(resourceProvider);
            if ($stateParams.isNew) {
              return {id: $stateParams.id};
            }
            return firstValueFrom(resource.get($stateParams.id));
          }
        }, _.get(aExtras, 'resolve', {})),
        views: _.assign({}, views, _.get(aExtras, 'views')),
        data: _.assign({}, _.get(aExtras, 'data', {}), {
          title: _.get(aExtras, ['data', 'title'], _.camelCase(state))
        })
      };
    };

    return rh;
  }

  return {
    services: new FeatureStateDependencies(ng2Injector),
    createRouterHelper(states, resourceProvider, parent, dir, listTemplate) {
      return createRouterStateHelper(states, resourceProvider, null, parent, dir, listTemplate);
    },
    createRouterAndChildHelper(states, resourceProvider, childProvider, parent, dir, listTemplate) {
      return createRouterStateHelper(states, resourceProvider, childProvider, parent, dir, listTemplate);
    }
  };
}
